// Pro rata holiday allowance: someone who starts part-way through a leave year gets a share of the year's allowance.
//
// The suggestion is the yearly allowance times the fraction of the leave year that is left, counting calendar days from
// their first day to the end of the leave year, rounded to the nearest half day. For example 24 days for someone starting
// on 3 November 2026 (59 of 365 days left) is 24 x 59 / 365 = 3.9, so 4 days. It is only a suggestion: HR can type any
// number, and the app stores that number, not the formula.

import type { ISODate } from '../types';
import { fromISO, leaveYearLabel, leaveYearOf, leaveYearRange } from './dates';

const DAY_MS = 864e5;
/** Whole days from a to b (b - a). Rounded so a daylight-saving change in between cannot make it off by one. */
const daysBetween = (a: ISODate, b: ISODate): number => Math.round((fromISO(b).getTime() - fromISO(a).getTime()) / DAY_MS);

export const roundToHalf = (n: number): number => Math.round(n * 2) / 2;

export interface ProRata {
  /** The leave year the start date falls in (the year it starts in, e.g. 2026 or 2026 for 2026/27). */
  year: number;
  /** "2026", or "Apr 2026 – Mar 2027" when the leave year doesn't start in January. */
  yearLabel: string;
  /** False when they start on the first day of their leave year (or earlier): they get the full allowance, nothing to pro rate. */
  partWay: boolean;
  /** Calendar days from their first day to the end of the leave year, and in the whole leave year. */
  daysLeft: number;
  daysInYear: number;
  /** The suggested allowance for that leave year, to the nearest half day (never more than the full allowance). */
  days: number;
}

export function proRata(annualAllowance: number, startDate: ISODate, leaveYearStartMonth: number): ProRata {
  const year = leaveYearOf(startDate, leaveYearStartMonth);
  const { from, to } = leaveYearRange(year, leaveYearStartMonth);
  const daysInYear = daysBetween(from, to) + 1;
  const daysLeft = daysBetween(startDate, to) + 1;
  const partWay = startDate > from;
  const days = partWay ? Math.min(annualAllowance, roundToHalf((annualAllowance * daysLeft) / daysInYear)) : annualAllowance;
  return { year, yearLabel: leaveYearLabel(year, leaveYearStartMonth), partWay, daysLeft, daysInYear, days };
}
